import { useWatch, type Control } from "react-hook-form";
import { useListAttributeKeys } from "@workspace/api-client-react";
import { AttributeKeyCombobox } from "@/components/inventory/attribute-key-combobox";
import { AttributeValueCombobox } from "@/components/inventory/attribute-value-combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Storage shape on the form's `variants[]` field array.
// Item code / stock / display name / status no longer surface in the row — server defaults handle them.
export interface VariantRowValue {
  attributeKeyId: number | null;
  attributeValueId: number | null;
  attributeValueText?: string;
  itemCode?: string | null;
  displayName?: string | null;
  currentStock: number;
  // 2026-05-18: per-variant price override. null = inherit parent item.sellingPrice.
  sellingPrice?: number | null;
  status: "Active" | "Discontinued";
  // Local-only auxiliary fields for the compound value inputs (Volume/Weight number + unit).
  // Not sent to the API — `attributeValueText` carries the composed string ("500 ml", "100 gram").
  _qty?: number | null;
  _unit?: string;
}

interface Props {
  index: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: (name: string, value: any) => void;
  onRemove: () => void;
  // Optional inline action (edit mode renders a "Save" button per pending row)
  trailingAction?: React.ReactNode;
}

// Unit options per measurement-type attribute key
const VOLUME_UNITS = ["ml", "l"] as const;
const WEIGHT_UNITS = ["gram", "kilogram"] as const;

export function CreateVariantRow({ index, control, setValue, onRemove, trailingAction }: Props) {
  const attributeKeyId = useWatch({ control, name: `variants.${index}.attributeKeyId` });
  const attributeValueId = useWatch({ control, name: `variants.${index}.attributeValueId` });
  const qty = useWatch({ control, name: `variants.${index}._qty` });
  const unit = useWatch({ control, name: `variants.${index}._unit` });
  const sellingPrice = useWatch({ control, name: `variants.${index}.sellingPrice` });

  const { data: attributeKeys } = useListAttributeKeys();
  const selectedKey = attributeKeys?.find((k) => k.id === attributeKeyId);
  const keyName = selectedKey?.name?.toLowerCase() ?? "";

  // Compose attributeValueText whenever qty/unit changes for the compound-input keys
  function setCompoundValue(nextQty: number | null, nextUnit: string | undefined, mode: "volume" | "weight" | "piece") {
    setValue(`variants.${index}._qty`, nextQty);
    if (mode !== "piece") setValue(`variants.${index}._unit`, nextUnit);
    if (nextQty == null) {
      setValue(`variants.${index}.attributeValueText`, "");
      return;
    }
    if (mode === "piece") {
      setValue(`variants.${index}.attributeValueText`, String(nextQty));
    } else {
      setValue(`variants.${index}.attributeValueText`, `${nextQty} ${nextUnit ?? ""}`.trim());
    }
    // Clear any prior attributeValueId — text path will create/look-up the value server-side
    setValue(`variants.${index}.attributeValueId`, null);
  }

  return (
    <div className="rounded border p-3 bg-muted/20">
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Attribute Key</p>
          <AttributeKeyCombobox
            value={attributeKeyId ?? null}
            onChange={(v) => {
              setValue(`variants.${index}.attributeKeyId`, v);
              setValue(`variants.${index}.attributeValueId`, null);
              setValue(`variants.${index}.attributeValueText`, "");
              setValue(`variants.${index}._qty`, null);
              setValue(`variants.${index}._unit`, undefined);
            }}
          />
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Attribute Value</p>
          {keyName === "volume" || keyName === "weight" ? (
            <div className="flex gap-2">
              <Input
                type="number"
                min={0}
                placeholder="e.g. 500"
                className="w-28"
                value={qty ?? ""}
                onChange={(e) => setCompoundValue(
                  e.target.value ? Number(e.target.value) : null,
                  unit ?? (keyName === "volume" ? "ml" : "gram"),
                  keyName === "volume" ? "volume" : "weight",
                )}
              />
              <Select
                value={unit ?? (keyName === "volume" ? "ml" : "gram")}
                onValueChange={(v) => setCompoundValue(qty ?? null, v, keyName === "volume" ? "volume" : "weight")}
              >
                <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(keyName === "volume" ? VOLUME_UNITS : WEIGHT_UNITS).map((u) => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : keyName === "piece" ? (
            <Input
              type="number"
              min={1}
              placeholder="e.g. 6"
              value={qty ?? ""}
              onChange={(e) => setCompoundValue(e.target.value ? Number(e.target.value) : null, undefined, "piece")}
            />
          ) : (
            <AttributeValueCombobox
              attributeKeyId={attributeKeyId ?? null}
              value={attributeValueId ?? null}
              onChange={(v) => setValue(`variants.${index}.attributeValueId`, v)}
            />
          )}
        </div>
        <div className="flex flex-col gap-1 items-end">
          {trailingAction}
          <Button type="button" variant="ghost" size="sm" onClick={onRemove} className="text-destructive hover:text-destructive text-xs">
            Remove
          </Button>
        </div>
      </div>
      {/* 2026-05-18: per-variant price override. Optional — leave blank to inherit
          the parent item's selling price. Useful for service variants priced differently
          (e.g. Print A4 B&W ₹2 vs Color ₹10). */}
      <div className="mt-2">
        <p className="text-xs text-muted-foreground mb-1">
          Variant Price <span className="text-muted-foreground/70">(optional — overrides parent)</span>
        </p>
        <Input
          type="number"
          min={0}
          step="0.01"
          placeholder="Inherits parent price"
          value={sellingPrice ?? ""}
          onChange={(e) => setValue(
            `variants.${index}.sellingPrice`,
            e.target.value === "" ? null : Number(e.target.value),
          )}
          className="max-w-[200px]"
        />
      </div>
    </div>
  );
}
